import React, { useState } from 'react';
import { graphql } from 'gatsby';
import { IFontIconCard } from '@ecs/tng-common/interfaces';
import StickyBannerDynamic from '../components/StickyBannerDynamic';
import LayoutFull from '../components/LayoutFull';
import LatestPostsList, { latestPostsLimit } from '../components/LatestPosts';
import HeroSection from '../components/HeroSection';
import FeaturedPosts from '../components/FeaturedPosts';
import ActionCardsSlider from '../components/ActionCardsSlider';
import PopularPosts from '../components/PopularPosts';
import BlogCategoryLinksProps from '../components/BlogCategoryLinks';
import SEO from '../components/SEO';
import { exploreMoreLinks, heroSections } from '../content/blog-category-links';
import { actionCards, IActionCardsContent } from '../content/action-cards';
import { setDataSection, setEcsSiteSection } from '../helpers/analytics';
import {
  categorySlugsWithScoreSearch,
  removeDuplicatedPosts,
  setSeoMetaTags
} from '../helpers/functions';
import '../styles/category-template.scss';
import { IPost } from '../helpers/interfaces';
import CreditScoreSearch from '../components/CreditScoreSearch';
import BoostCtaModal from '../components/BoostCtaModal';

interface PageTemplateProps {
  pageContext: {
    catId: number;
    catName: string;
    catSlug: string;
    currentPath: string;
    childrenCategories: number[];
    allCategories: number[];
  };
  data: any;
}
//
export default function CategoryTemplate({ pageContext, data }: PageTemplateProps) {
  const [toShowModal, setToShowModal] = useState(false);

  const { catSlug, catName, currentPath, catId, childrenCategories } = pageContext;
  const description = data?.wpCategory?.description;
  const featuredPosts = data?.wpCategory?.featuredPosts || [];
  const popularPosts = data?.wpCategory?.popularPosts || [];
  const latestPosts = data?.latestPosts;
  // Filter popularPosts to remove any duplicates from featuredPosts
  const featuredPostsSliced: IPost[] = featuredPosts?.slice(0, 9);
  const popularPostsFiltered =
    popularPosts.length > 0
      ? removeDuplicatedPosts(popularPosts, featuredPostsSliced)?.slice(0, 6)
      : [];
  // Filter latestPosts to remove any duplicates from popularPosts and featured posts
  // Since the popular articles component is not displayed when there is only one post, we remove the popular post ID from the latest posts exclusion list in such cases.
  const articlesToExclude =
    popularPostsFiltered.length > 1
      ? featuredPostsSliced.concat(popularPostsFiltered)
      : featuredPostsSliced;
  const filteredLatestPosts =
    latestPosts?.nodes?.length > 0
      ? removeDuplicatedPosts(latestPosts.nodes, articlesToExclude)
      : [];

  const actionCardsData: IActionCardsContent = actionCards[catSlug];
  const dataForExploreMore: IFontIconCard[] = exploreMoreLinks[catSlug] || [];
  const toggleModal = () => {
    setToShowModal((prevValue) => !prevValue);
  };
  return (
    <LayoutFull>
      <HeroSection
        content={heroSections[catSlug] || []}
        heading={catName}
        subheading={description}
        theme="dark"
        background="textured"
      />
      <StickyBannerDynamic
        category={catSlug}
        categoryId={catId}
        buttonCTAclick={() => {
          toggleModal();
        }}
        variant="light"
      />
      {featuredPostsSliced.length > 0 ? (
        <FeaturedPosts
          theme={!actionCardsData && popularPostsFiltered?.length > 1 ? 'white' : 'coconut'}
          textured={false}
          toHideCategory={!(childrenCategories.length > 1)}
          heading="Editors' picks"
          content={featuredPostsSliced}
        />
      ) : null}
      {actionCardsData ? (
        <ActionCardsSlider
          catId={catId}
          content={actionCardsData}
          heading="Take action with these tools"
        />
      ) : null}
      {popularPostsFiltered?.length > 1 ? (
        <PopularPosts
          content={popularPostsFiltered}
          heading="What others are reading"
          theme="coconut"
          toHideCategory={!(childrenCategories.length > 1)}
          textured={false}
        />
      ) : null}

      {dataForExploreMore.length > 0 ? (
        <div
          className={`container container-py ${filteredLatestPosts?.length ? 'pb-5 pb-lg-7' : ''} ${popularPostsFiltered?.length < 2 ? 'border-top' : ''}`}>
          <BlogCategoryLinksProps links={dataForExploreMore} />
        </div>
      ) : null}
      {filteredLatestPosts.length > 0 ? (
        <LatestPostsList
          catSlug={catSlug}
          toHideCategory={!(childrenCategories.length > 1)}
          numberOfPosts={filteredLatestPosts.length}
          currentPath={currentPath}
          initPosts={filteredLatestPosts?.slice(0, latestPostsLimit)}
          className={`
            ${popularPosts.length < 2 && dataForExploreMore.length === 0 ? 'border-top' : ''} ${dataForExploreMore.length > 0 ? 'pt-0' : ''}
          `}
          excludeIds={articlesToExclude.map((post) => post.databaseId)}
        />
      ) : null}
      {categorySlugsWithScoreSearch.includes(catSlug) ? (
        <div className="container container-py pt-0">
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-9 col-xl-8">
              <CreditScoreSearch variant="coconut" />
            </div>
          </div>
        </div>
      ) : null}
      <BoostCtaModal
        show={toShowModal}
        onHide={toggleModal}
      />
    </LayoutFull>
  );
}
export function Head({ pageContext, data }: PageTemplateProps) {
  const { catSlug, catId } = pageContext;
  const seo = data.wpCategory?.expSeo;
  const title = seo?.title || '';
  const description = seo?.description || '';
  const schema = seo?.schema || '';
  const schemaScript = schema && <script type="application/ld+json">{schema}</script>;
  const section = setDataSection(catSlug);
  const category = catSlug;
  return (
    <>
      <body
        data-section={section}
        data-category={category}
        data-id={catId.toString()}
        data-op="EXP-ASK-CAT"
        data-ecscategory={setEcsSiteSection('category', { category, section })}
      />
      <SEO
        title={title}
        description={description}
        noindex={seo?.metaRobotsNoindex !== 'index'}>
        {seo && setSeoMetaTags(seo)}
        {schemaScript}
      </SEO>
    </>
  );
}

export const query = graphql`
  query GetPostsPerCategory($catId: Int!, $excludeCategories: [String!], $allCategories: [Int!]) {
    latestPosts: allWpPost(
      filter: {
        categories: {
          nodes: {
            elemMatch: { slug: { nin: $excludeCategories }, databaseId: { in: $allCategories } }
          }
        }
      }
      sort: { date: DESC }
    ) {
      nodes {
        ...PostPreviewContent
        ...ByLineAuthor
        ...ByLineCategory
      }
    }
    wpCategory(databaseId: { eq: $catId }) {
      popularPosts {
        ...PostPreviewContent
        ...ByLineAuthor
        ...ByLineCategory
      }
      featuredPosts {
        ...PostPreviewContent
        ...ByLineAuthor
        ...ByLineCategory
      }
      expSeo {
        ...ExpSEOFields
      }
      description
    }
  }
`;
